import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Save, Plus, Trash2 } from "lucide-react";

const PACKAGING_OPTIONS = [
  { value: "120x80",  label: "Euro 120 × 80 cm" },
  { value: "120x110", label: "Industrie 120 × 110 cm" },
  { value: "160x80",  label: "Sondermass 160 × 80 cm" },
  { value: "110x80",  label: "Industrie Halbpalette 110 × 80 cm" },
  { value: "60x80",   label: "Halbpalette 60 × 80 cm" },
];

const EVEHICLE_OPTIONS = [
  { value: "efz_2rad", label: "E-Fahrzeug 2 Rad" },
  { value: "efz_3rad", label: "E-Fahrzeug 3 Rad" },
  { value: "efz_4rad", label: "E-Fahrzeug 4 Rad" },
];

const TIER_OPTIONS = [
  { value: "bis_150kg",   label: "1. Position bis 150 kg" },
  { value: "bis_250kg",   label: "2. Positionen bis 250 kg" },
  { value: "ueber_250kg", label: "3. Positionen über 250 kg" },
];

const emptyPosition = () => ({
  packaging_type: "120x80",
  quantity: 1,
  height_cm: "",
  weight_kg: "",
  product_identification: "",
  description: "",
  is_half_pallet: false,
});

const emptyServices = () => ({
  tier: "",
  delivery_ground_floor: false,
  delivery_floor_no_lift: false,
  delivery_above_third_floor: false,
  slot_booking_count: 0,
  pallet_exchange_count: 0,
  scheduled_delivery: false,
  second_delivery_pallets: 0,
  intermediate_storage_chf: 0,
});

const Section = ({ title, children }) => (
  <div className="card p-6">
    <div className="label-eyebrow mb-4" style={{ color: "#D4AF37" }}>{title}</div>
    {children}
  </div>
);
const Field = ({ label, children }) => (
  <div>
    <label className="label-eyebrow block mb-2">{label}</label>
    {children}
  </div>
);

const emptyForm = {
  customer_id: "",
  pickup: { name: "", address: "", zip: "", city: "", country: "CH" },
  delivery: { name: "", address: "", zip: "", city: "", country: "CH" },
  contact_person: "",
  contact_phone: "",
  delivery_date: new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 10),
  delivery_time: "",
  reference_number: "",
  customer_order_number: "",
  pallet_count: 1,
  weight_kg: "",
  notes: "",
  price_chf: "",
  recipient_email: "",
  route_id: "",
  positions: [emptyPosition()],
  services: emptyServices(),
};

export default function OrderCreate({ editMode = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isAdmin = user?.role === "admin" || user?.role === "dispatcher";
  const basePath = user?.role === "customer" ? "/portal/orders" : "/admin/orders";

  const [customers, setCustomers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(editMode);
  const [form, setForm] = useState(emptyForm);
  const [quickPickups, setQuickPickups] = useState([]);
  const [quickDeliveries, setQuickDeliveries] = useState([]);
  const [customerRoutes, setCustomerRoutes] = useState([]);
  const [customerPricingModel, setCustomerPricingModel] = useState("tier"); // "tier" | "flat"
  const [evehicleEnabled, setEvehicleEnabled] = useState(false);

  useEffect(() => {
    if (isAdmin) api.get("/customers").then(({ data }) => setCustomers(data));
  }, [isAdmin]);

  // When customer changes, load their pricing model + routes
  useEffect(() => {
    const cid = isAdmin ? form.customer_id : user?.customer_id;
    if (!cid) { setCustomerRoutes([]); setCustomerPricingModel("tier"); setEvehicleEnabled(false); return; }
    Promise.all([
      api.get(`/customers/${cid}/pricing`).then(r => r.data).catch(() => ({})),
      api.get(`/customers/${cid}/routes`).then(r => r.data).catch(() => []),
      api.get(`/customers/${cid}/features`).then(r => r.data).catch(() => ({})),
    ]).then(([pr, routes, feat]) => {
      setCustomerPricingModel(pr?.model || "tier");
      setCustomerRoutes(routes || []);
      setEvehicleEnabled(!!feat?.evehicle_enabled);
    });
  }, [isAdmin, form.customer_id, user]);

  useEffect(() => {
    if (editMode) return; // no quick-pick when editing existing order
    const cid = isAdmin ? form.customer_id : undefined;
    if (isAdmin && !cid) {
      setQuickPickups([]); setQuickDeliveries([]);
      return;
    }
    (async () => {
      const params = cid ? { customer_id: cid } : {};
      try {
        const [pu, dv] = await Promise.all([
          api.get("/address-book", { params: { ...params, kind: "pickup" } }),
          api.get("/address-book", { params: { ...params, kind: "delivery" } }),
        ]);
        setQuickPickups(pu.data.slice(0, 4));
        setQuickDeliveries(dv.data.slice(0, 4));
      } catch { /* ignore */ }
    })();
  }, [editMode, isAdmin, form.customer_id]);

  useEffect(() => {
    if (!editMode || !editId) return;
    (async () => {
      try {
        const { data } = await api.get(`/orders/${editId}`);
        setForm({
          customer_id: data.customer_id || "",
          pickup: data.pickup || emptyForm.pickup,
          delivery: data.delivery || emptyForm.delivery,
          contact_person: data.contact_person || "",
          contact_phone: data.contact_phone || "",
          delivery_date: data.delivery_date || "",
          delivery_time: data.delivery_time || "",
          reference_number: data.reference_number || "",
          customer_order_number: data.customer_order_number || "",
          pallet_count: data.pallet_count || 1,
          weight_kg: data.weight_kg ?? 0,
          notes: data.notes || "",
          price_chf: data.price_chf ?? "",
          recipient_email: data.recipient_email || "",
          positions: data.positions && data.positions.length ? data.positions.map(p => ({
            packaging_type: p.packaging_type || "120x80",
            quantity: p.quantity || 1,
            height_cm: p.height_cm ?? "",
            weight_kg: p.weight_kg ?? "",
            product_identification: p.product_identification || "",
            description: p.description || "",
            is_half_pallet: !!p.is_half_pallet,
          })) : [emptyPosition()],
          services: data.services ? {
            tier: data.services.tier || "",
            delivery_ground_floor: !!data.services.delivery_ground_floor,
            delivery_floor_no_lift: !!data.services.delivery_floor_no_lift,
            delivery_above_third_floor: !!data.services.delivery_above_third_floor,
            slot_booking_count: data.services.slot_booking_count || 0,
            pallet_exchange_count: data.services.pallet_exchange_count || 0,
            scheduled_delivery: !!data.services.scheduled_delivery,
            second_delivery_pallets: data.services.second_delivery_pallets || 0,
            intermediate_storage_chf: data.services.intermediate_storage_chf || 0,
          } : emptyServices(),
          route_id: data.route_id || "",
        });
      } catch (e) { setError(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [editMode, editId]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setAddr = (block, key, val) => setForm((f) => ({ ...f, [block]: { ...f[block], [key]: val } }));
  const setPos = (idx, key, val) => setForm((f) => ({
    ...f,
    positions: f.positions.map((p, i) => (i === idx ? { ...p, [key]: val } : p)),
  }));
  const addPos = () => setForm((f) => ({ ...f, positions: [...f.positions, emptyPosition()] }));
  const removePos = (idx) => setForm((f) => ({
    ...f,
    positions: f.positions.length > 1 ? f.positions.filter((_, i) => i !== idx) : f.positions,
  }));
  const setService = (key, val) => setForm((f) => ({ ...f, services: { ...f.services, [key]: val } }));

  // Derived: total pallets & total weight (kg) from positions
  const totalPallets = form.positions.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
  const totalPositionWeight = form.positions.reduce(
    (s, p) => s + (Number(p.weight_kg) || 0) * (Number(p.quantity) || 0),
    0,
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      // Clean positions: drop completely empty rows, coerce numerics, drop empty optional strings
      const cleanedPositions = form.positions
        .filter((p) => p.packaging_type && Number(p.quantity) > 0)
        .map((p) => {
          const out = {
            packaging_type: p.packaging_type,
            quantity: Number(p.quantity),
          };
          if (p.height_cm !== "" && p.height_cm != null) out.height_cm = Number(p.height_cm);
          if (p.weight_kg !== "" && p.weight_kg != null) out.weight_kg = Number(p.weight_kg);
          if (p.product_identification) out.product_identification = p.product_identification;
          if (p.description) out.description = p.description;
          return out;
        });

      // Clean services: only include when something actually selected
      const s = form.services || {};
      const hasService = s.tier || s.delivery_ground_floor || s.delivery_floor_no_lift || s.delivery_above_third_floor;
      const cleanedServices = hasService
        ? {
            tier: s.tier || null,
            delivery_ground_floor: !!s.delivery_ground_floor,
            delivery_floor_no_lift: !!s.delivery_floor_no_lift,
            delivery_above_third_floor: !!s.delivery_above_third_floor,
          }
        : null;

      // Total weight: when positions carry weight, ALWAYS use the position sum (source of truth).
      // Otherwise fall back to the manual field.
      const manualWeight = Number(form.weight_kg);
      const effectiveWeight = totalPositionWeight > 0
        ? totalPositionWeight
        : (manualWeight > 0 ? manualWeight : 0);

      const payload = {
        ...form,
        weight_kg: effectiveWeight,
        price_chf: form.price_chf === "" || form.price_chf === null ? null : Number(form.price_chf),
        route_id: form.route_id || null,
        positions: cleanedPositions.length ? cleanedPositions : null,
        services: cleanedServices,
      };
      // Strip empty optional strings so backend treats them as missing (not invalid).
      ["recipient_email", "customer_order_number", "reference_number", "delivery_time", "notes", "contact_person", "contact_phone", "route_id"].forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) delete payload[k];
      });
      if (payload.positions == null) delete payload.positions;
      if (payload.services == null) delete payload.services;
      if (editMode) {
        // Cannot change pallet_count or customer when editing (would invalidate barcodes)
        const { pallet_count, customer_id, ...editable } = payload;
        const { data } = await api.put(`/orders/${editId}`, editable);
        navigate(`${basePath}/${data.id}`);
      } else {
        payload.pallet_count = Number(form.pallet_count);
        if (!isAdmin) delete payload.customer_id;
        const { data } = await api.post("/orders", payload);
        navigate(`${basePath}/${data.id}`);
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Layout><div className="px-6 py-8 text-[var(--text-3)]" data-testid="order-form-loading">Laden…</div></Layout>;
  }

  const title = editMode ? "Auftrag bearbeiten" : "Auftrag erfassen";
  const eyebrow = editMode ? "Bearbeiten" : "Neuer Auftrag";

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-5xl mx-auto fade-in">
        <button onClick={() => navigate(-1)} data-testid="order-form-back" className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--text)]">
          <ArrowLeft size={14} strokeWidth={1.5} /> Zurück
        </button>
        <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>{eyebrow}</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">{title}</h1>
            <p className="text-sm text-[var(--text-3)] mt-1">
              {editMode ? "Auftragsnummer und Paletten-Barcodes bleiben unverändert." : "Auftragsnummer und Paletten-Barcodes werden automatisch generiert."}
            </p>
          </div>
        </div>

        {error && (
          <div data-testid="order-form-error" className="mt-6 border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          {isAdmin && !editMode && (
            <Section title="Kunde">
              <Field label="Kunde wählen *">
                <select
                  required
                  data-testid="order-create-customer-select"
                  value={form.customer_id}
                  onChange={(e) => set("customer_id", e.target.value)}
                  className="input-base"
                >
                  <option value="">— wählen —</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </Field>
            </Section>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Section title="Abholadresse">
              {quickPickups.length > 0 && (
                <div className="mb-4 -mt-1">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#D4AF37" }} />
                    Schnellauswahl · Top {quickPickups.length}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickPickups.map((e) => (
                      <button
                        type="button"
                        key={e.id}
                        onClick={() => set("pickup", { name: e.name, address: e.address, zip: e.zip || "", city: e.city || "", country: e.country || "CH" })}
                        data-testid={`quick-pickup-${e.id}`}
                        className="px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors text-[var(--text)] hover:text-[#D4AF37]"
                        title={`${e.address}, ${e.zip || ""} ${e.city || ""}`}
                      >
                        {e.name} <span className="text-[var(--text-3)] ml-1">· {e.zip} {e.city}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <AddressAutocomplete
                value={form.pickup}
                onChange={(v) => set("pickup", v)}
                kind="pickup"
                customerId={isAdmin ? form.customer_id : undefined}
                testidPrefix="pickup"
              />
            </Section>

            <Section title="Lieferadresse">
              {quickDeliveries.length > 0 && (
                <div className="mb-4 -mt-1">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#D4AF37" }} />
                    Schnellauswahl · Top {quickDeliveries.length}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {quickDeliveries.map((e) => (
                      <button
                        type="button"
                        key={e.id}
                        onClick={() => set("delivery", { name: e.name, address: e.address, zip: e.zip || "", city: e.city || "", country: e.country || "CH" })}
                        data-testid={`quick-delivery-${e.id}`}
                        className="px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm transition-colors text-[var(--text)] hover:text-[#D4AF37]"
                        title={`${e.address}, ${e.zip || ""} ${e.city || ""}`}
                      >
                        {e.name} <span className="text-[var(--text-3)] ml-1">· {e.zip} {e.city}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <AddressAutocomplete
                value={form.delivery}
                onChange={(v) => set("delivery", v)}
                kind="delivery"
                customerId={isAdmin ? form.customer_id : undefined}
                testidPrefix="delivery"
              />
            </Section>
          </div>

          <Section title="Positionen">
            <div className="space-y-3">
              {form.positions.map((p, idx) => (
                <div
                  key={idx}
                  data-testid={`position-row-${idx}`}
                  className="border border-[var(--border)] rounded-sm p-3 bg-[var(--surface-2)]"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">
                      Position {idx + 1}
                    </div>
                    {form.positions.length > 1 && !editMode && (
                      <button
                        type="button"
                        onClick={() => removePos(idx)}
                        data-testid={`position-remove-${idx}`}
                        className="text-[var(--text-3)] hover:text-red-700 flex items-center gap-1 text-xs uppercase tracking-wider"
                      >
                        <Trash2 size={13} strokeWidth={1.5} /> Entfernen
                      </button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-4">
                      <Field label="Verpackungstyp *">
                        <select
                          required
                          data-testid={`position-packaging-${idx}`}
                          className="input-base"
                          value={p.packaging_type}
                          onChange={(e) => setPos(idx, "packaging_type", e.target.value)}
                          disabled={editMode}
                        >
                          {(evehicleEnabled || String(p.packaging_type).startsWith("efz_")
                            ? [...PACKAGING_OPTIONS, ...EVEHICLE_OPTIONS]
                            : PACKAGING_OPTIONS
                          ).map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Menge *">
                        <input
                          required
                          type="number"
                          min={1}
                          data-testid={`position-quantity-${idx}`}
                          className="input-base"
                          value={p.quantity}
                          onChange={(e) => setPos(idx, "quantity", e.target.value)}
                          disabled={editMode}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Höhe (cm)">
                        <input
                          type="number"
                          min={0}
                          step="0.1"
                          data-testid={`position-height-${idx}`}
                          className="input-base"
                          value={p.height_cm}
                          onChange={(e) => setPos(idx, "height_cm", e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-2">
                      <Field label="Gewicht (kg)">
                        <input
                          type="number"
                          min={0}
                          step="0.1"
                          data-testid={`position-weight-${idx}`}
                          className="input-base"
                          placeholder="pro Stück"
                          value={p.weight_kg}
                          onChange={(e) => setPos(idx, "weight_kg", e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-4">
                      <Field label="Artikel / Identifikation">
                        <input
                          data-testid={`position-product-${idx}`}
                          className="input-base"
                          placeholder="z.B. Artikelnummer"
                          value={p.product_identification}
                          onChange={(e) => setPos(idx, "product_identification", e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-12">
                      <Field label="Beschreibung">
                        <input
                          data-testid={`position-description-${idx}`}
                          className="input-base"
                          placeholder="Inhalt, Hinweise, …"
                          value={p.description}
                          onChange={(e) => setPos(idx, "description", e.target.value)}
                        />
                      </Field>
                    </div>
                    <div className="md:col-span-12">
                      <label className="flex items-center gap-3 text-sm text-[var(--text-2)] hover:text-[var(--text)] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!p.is_half_pallet}
                          data-testid={`position-half-${idx}`}
                          onChange={(e) => setPos(idx, "is_half_pallet", e.target.checked)}
                          className="accent-[#D4AF37] w-4 h-4"
                        />
                        <span>½ Palette (eigener Preis-Tier in der Abrechnung)</span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
              {!editMode && (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-1">
                  <button
                    type="button"
                    onClick={addPos}
                    data-testid="position-add"
                    className="text-xs uppercase tracking-wider text-[#D4AF37] hover:text-[var(--text)] flex items-center justify-center gap-2 border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 px-3 py-2 rounded-sm"
                  >
                    <Plus size={14} strokeWidth={1.8} /> Position hinzufügen
                  </button>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] uppercase tracking-wider text-[var(--text-2)]">
                    <span>
                      Total Paletten:
                      <span data-testid="total-pallets" className="text-[#D4AF37] font-mono ml-2">{totalPallets}</span>
                    </span>
                    <span>
                      Total Gewicht:
                      <span data-testid="total-weight" className="text-[#D4AF37] font-mono ml-2">
                        {totalPositionWeight.toFixed(1)} kg
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Liefer- & Entsorgungsservice">
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-2">Servicekategorie</div>
                <div className="grid md:grid-cols-3 gap-2">
                  {TIER_OPTIONS.map((o) => (
                    <label
                      key={o.value}
                      data-testid={`service-tier-${o.value}`}
                      className={`flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer text-sm transition-colors ${
                        form.services.tier === o.value
                          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[var(--text)]"
                          : "border-[var(--border)] text-[var(--text-2)] hover:border-[var(--border-strong)]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="service-tier"
                        value={o.value}
                        checked={form.services.tier === o.value}
                        onChange={() => setService("tier", o.value)}
                        className="accent-[#D4AF37]"
                      />
                      {o.label}
                    </label>
                  ))}
                </div>
                {form.services.tier && (
                  <button
                    type="button"
                    onClick={() => setService("tier", "")}
                    data-testid="service-tier-clear"
                    className="mt-2 text-[10px] uppercase tracking-wider text-[var(--text-3)] hover:text-[#D4AF37]"
                  >
                    Auswahl zurücksetzen
                  </button>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-2">Zusatzoptionen</div>
                <div className="space-y-2">
                  {[
                    { key: "delivery_ground_floor", label: "Lieferung an Verwendungsort (ebenerdig)" },
                    { key: "delivery_floor_no_lift", label: "Stockwerklieferung ohne Lift (pro Etage)" },
                    { key: "delivery_above_third_floor", label: "ab 3. Etage" },
                  ].map((opt) => (
                    <label
                      key={opt.key}
                      data-testid={`service-${opt.key}`}
                      className="flex items-center gap-3 cursor-pointer text-sm text-[var(--text)] hover:text-[var(--text)]"
                    >
                      <input
                        type="checkbox"
                        checked={!!form.services[opt.key]}
                        onChange={(e) => setService(opt.key, e.target.checked)}
                        className="accent-[#D4AF37] w-4 h-4"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              {isAdmin && (
                <div className="pt-4 border-t border-[var(--border)]">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mb-2">Abrechnungs-Zusatzleistungen</div>
                  <div className="grid md:grid-cols-3 gap-3">
                    <Field label="Slot-Buchungen (Anz.)">
                      <input type="number" min={0} step="1" data-testid="svc-slot-booking" className="input-base font-mono" value={form.services.slot_booking_count || 0} onChange={(e) => setService("slot_booking_count", Number(e.target.value || 0))} />
                    </Field>
                    <Field label="Palettentausch (Anz.)">
                      <input type="number" min={0} step="1" data-testid="svc-pallet-exchange" className="input-base font-mono" value={form.services.pallet_exchange_count || 0} onChange={(e) => setService("pallet_exchange_count", Number(e.target.value || 0))} />
                    </Field>
                    <Field label="Zweitzustellung (Pal.)">
                      <input type="number" min={0} step="1" data-testid="svc-second-delivery" className="input-base font-mono" value={form.services.second_delivery_pallets || 0} onChange={(e) => setService("second_delivery_pallets", Number(e.target.value || 0))} />
                    </Field>
                    <Field label="Zwischenlagerung (CHF)">
                      <input type="number" min={0} step="0.5" data-testid="svc-storage" className="input-base font-mono" value={form.services.intermediate_storage_chf || 0} onChange={(e) => setService("intermediate_storage_chf", Number(e.target.value || 0))} />
                    </Field>
                    <div className="md:col-span-2 flex items-end">
                      <label className="flex items-center gap-3 text-sm cursor-pointer">
                        <input type="checkbox" data-testid="svc-scheduled" checked={!!form.services.scheduled_delivery} onChange={(e) => setService("scheduled_delivery", e.target.checked)} className="accent-[#D4AF37] w-4 h-4" />
                        Lieferung auf Termin
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Kontakt & Lieferung">
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Ansprechperson"><input data-testid="contact-person" className="input-base" placeholder="Optional" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} /></Field>
              <Field label="Telefon"><input data-testid="contact-phone" className="input-base" placeholder="Optional" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
              <Field label="Lieferdatum *"><input required type="date" data-testid="delivery-date" className="input-base" value={form.delivery_date} onChange={(e) => set("delivery_date", e.target.value)} /></Field>
              <Field label="Lieferzeit"><input data-testid="delivery-time" className="input-base" placeholder="z.B. 08:00 - 12:00" value={form.delivery_time || ""} onChange={(e) => set("delivery_time", e.target.value)} /></Field>
              <Field label="Referenz Nr."><input data-testid="reference-number" className="input-base" value={form.reference_number || ""} onChange={(e) => set("reference_number", e.target.value)} /></Field>
              <Field label="Bestell-Nr. (Kunde)"><input data-testid="customer-order-number" className="input-base" placeholder="Optional · Ihre interne Bestellnummer" value={form.customer_order_number || ""} onChange={(e) => set("customer_order_number", e.target.value)} /></Field>
              {!editMode ? (
                <Field label="Anzahl Paletten (auto aus Positionen)">
                  <input
                    disabled
                    data-testid="pallet-count"
                    className="input-base opacity-70 cursor-not-allowed font-mono"
                    value={totalPallets}
                  />
                </Field>
              ) : (
                <Field label="Anzahl Paletten (nicht änderbar)">
                  <input disabled data-testid="pallet-count" className="input-base opacity-60 cursor-not-allowed" value={form.pallet_count} />
                </Field>
              )}
              <Field label={`Gewicht (kg)${totalPositionWeight > 0 ? " · auto aus Positionen" : ""}`}>
                <input
                  type="number"
                  min={0}
                  step="0.1"
                  data-testid="weight-kg"
                  className={`input-base ${totalPositionWeight > 0 ? "opacity-70 cursor-not-allowed font-mono" : ""}`}
                  placeholder={totalPositionWeight > 0 ? "" : "z.B. 120"}
                  disabled={totalPositionWeight > 0}
                  value={totalPositionWeight > 0 ? totalPositionWeight.toFixed(1) : form.weight_kg}
                  onChange={(e) => set("weight_kg", e.target.value)}
                />
              </Field>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <Field label="Empfänger E-Mail (optional, für Status-Benachrichtigungen + Lieferschein)">
                <input type="email" data-testid="recipient-email" className="input-base" value={form.recipient_email || ""} onChange={(e) => set("recipient_email", e.target.value)} placeholder="empfaenger@example.ch" />
              </Field>
              {isAdmin && customerPricingModel === "flat" && customerRoutes.length > 0 && (
                <Field label="Strecke (Pauschalpreis)">
                  <select
                    data-testid="route-select"
                    className="input-base"
                    value={form.route_id || ""}
                    onChange={(e) => {
                      const rid = e.target.value;
                      set("route_id", rid);
                      // Auto-fill price field from route when selecting (unless user already typed)
                      if (rid && !form.price_chf) {
                        const r = customerRoutes.find((x) => x.id === rid);
                        if (r) set("price_chf", r.price_chf);
                      }
                      if (!rid) set("price_chf", "");
                    }}
                  >
                    <option value="">— Keine Strecke (manueller Preis) —</option>
                    {customerRoutes.map((r) => (
                      <option key={r.id} value={r.id}>{r.name} · CHF {Number(r.price_chf).toFixed(2)}</option>
                    ))}
                  </select>
                </Field>
              )}
              {isAdmin && (
                <Field label={`Preis (CHF)${customerPricingModel === "tier" ? " · überschreibt Staffelpreis" : ""}`}>
                  <input type="number" min={0} step="0.05" data-testid="price-chf" className="input-base" value={form.price_chf} onChange={(e) => set("price_chf", e.target.value)} placeholder={customerPricingModel === "flat" ? "Pauschalbetrag" : "Optional — sonst aus Staffelpreisen"} />
                </Field>
              )}
              <div className="md:col-span-2">
                <Field label="Bemerkungen">
                  <textarea data-testid="notes" rows={3} className="input-base" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
                </Field>
              </div>
            </div>
          </Section>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => navigate(-1)} data-testid="order-form-cancel" className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider">Abbrechen</button>
            <button type="submit" disabled={submitting} data-testid="order-form-submit" className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
              <Save size={16} strokeWidth={1.8} /> {submitting ? "Speichern…" : (editMode ? "Änderungen speichern" : "Auftrag erfassen")}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
